import React, { useState, useCallback, useEffect } from "react";
import Button from "~/app/common/Button";
import Icon from "~/app/common/Icon";
import classes from './style.module.scss';
import useTranslation from "~/app/hooks/useTranslation";
import { ChecklistItem } from "~/training/types";
import { PERMISSION_GROUPS } from "~/accessControl/permissionGroups";
import useAccessControl from "~/app/hooks/useAccessControl";


interface DynamicChecklistProps {
  initialItems?: ChecklistItem[];
  onChecklistChange: (items: ChecklistItem[]) => void;
}

const ItemActionGroup: React.FC<{ onCheckClick: () => void, onCancelClick: () => void }> = React.memo(({ onCheckClick, onCancelClick }) => (
  <>
    <Button onClick={onCheckClick} className={classes["action-button"]}>
      <Icon name="check" className={classes["check-icon"]} />
    </Button>
    <Button onClick={onCancelClick} className={classes["action-button"]}>
      <Icon name="close" className={classes["close-icon"]} />
    </Button>
  </>
));

export type DynamicChecklistComponent = React.FC<DynamicChecklistProps>

const DynamicChecklist: DynamicChecklistComponent = ({ initialItems = [], onChecklistChange }) => {

  const { t } = useTranslation("training");

  const { checkPermission } = useAccessControl();
  const addManualChecklistPermission = checkPermission(PERMISSION_GROUPS.addManualChecklist);

  const [items, setItems] = useState<ChecklistItem[]>(initialItems);

  const [editState, setEditState] = useState<{ id: string | null, text: string }>({ id: null, text: '' });
  const [newItemText, setNewItemText] = useState('');
  const [showAddField, setShowAddField] = useState(false);

  const updateItems = useCallback((newItems: ChecklistItem[]) => {
    setItems(newItems);
    onChecklistChange(newItems);
  }, [onChecklistChange]);

  const handleCheckChange = useCallback((id: string) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, isChecked: !item.isChecked } : item
    );
    updateItems(newItems);
  }, [items, updateItems]);

  const startEdit = useCallback((id: string, currentLabel: string) => {
    setEditState({ id, text: currentLabel });
  }, []);

  const saveEdit = useCallback(() => {
    if (editState.id && editState.text.trim()) {
      const newItems = items.map(item =>
        item.id === editState.id ? { ...item, title: editState.text.trim() } : item
      );
      updateItems(newItems);
    }
    setEditState({ id: null, text: '' });
  }, [editState, items, updateItems]);

  const cancelEdit = useCallback(() => {
    setEditState({ id: null, text: '' });
  }, []);

  const deleteItem = useCallback((id: string) => {
    const newItems = items.filter(item => item.id !== id);
    updateItems(newItems);
  }, [items, updateItems]);

  const addNewItem = useCallback(() => {
    if (newItemText.trim()) {
      const newItem: ChecklistItem = {
        id: 'Manual_' + Date.now().toString(),
        title: newItemText.trim(),
        isChecked: false,
      };
      const newItems = [...items, newItem];
      updateItems(newItems);
      setNewItemText('');
      setShowAddField(false);
    }
  }, [items, newItemText, updateItems]);

  const cancelAdd = useCallback(() => {
    setNewItemText('');
    setShowAddField(false);
  }, []);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  return (
    <div className={classes["checklist-container"]}>
      {items.map((item) => (
        <div key={item.id} className={classes["checklist-item"]}>
          <input className={classes["checkbox"]} id={item.id} type="checkbox" checked={item.isChecked} onChange={() => handleCheckChange(item.id)} />
          {addManualChecklistPermission && editState.id === item.id ? (
            <div className={classes["edit-container"]}>
              <input className={classes["edit-text-input"]} value={editState.text} onChange={(e) => setEditState({ id: item.id, text: e.target.value })} autoFocus required/>
              <ItemActionGroup onCheckClick={saveEdit} onCancelClick={cancelEdit} />
            </div>
          ) : (
            <div className={classes["text-container"]}>
              <div className={classes["item-text"]} onClick={() => handleCheckChange(item.id)}>{item.title}</div>
              {addManualChecklistPermission && (
                <div className={classes["button-container"]}>
                  <Button onClick={() => startEdit(item.id, item.title)} className={classes["action-button"]}><Icon name="edit"/></Button>
                  <Button onClick={() => deleteItem(item.id)} className={classes["action-button"]}><Icon name="delete"/></Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {addManualChecklistPermission && (
          showAddField ? (
            <div className={classes["add-item-container"]}>
              <input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder={`${t("addNewChecklist")} ...`}
                className={classes["edit-text-input"]}
                autoFocus
              />
              <ItemActionGroup onCheckClick={addNewItem} onCancelClick={cancelAdd} />
            </div>
          ) : (
            <Button className={classes['add-checklist-button']} onClick={() => setShowAddField(true)}>
              <Icon name="add" />
              <span> {t("addNewChecklist")}</span>
            </Button>
          )
      )}
    </div>
  );
};

export default DynamicChecklist;